import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { IntlShape } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import Checkbox from '@/shared/components/input/Checkbox';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import log from 'loglevel';
import ToastrService from '@/shared/services/ToastrService';
import classNames from 'classnames';
import moment from 'moment';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import EmailEditor from '@/shared/components/EmailEditor';
import enumCache from '@/shared/services/EnumCacheService';
import CollapsablePanel from '../../shared/components/CollapablePanel';
import messages from './taskMessages';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';

export default class ChangeTaskStatusModal extends Component {
  state = {
    emails: [],
    emailAttachments: [],
    selectedSubtask: undefined,
    selectedTaskStatus: 2,
    loadingTemplate: false,
    showEmailEditor: false,
    taskstatuses: [],
    autoLinkDocumentTypes: [],
  };

  componentDidMount() {
    this.form = React.createRef();
    const { intl } = this.props;
    enumCache.get('taskstatus').then(data => {
      this.setState({
        taskstatuses: [
          ...data.map(x => ({
            key: x.key,
            label: intl.formatMessage(listItemMsg('taskstatus', x.key, x.value)),
          })),
          {
            key: 4,
            label: intl.formatMessage(listItemMsg('extendedtaskstatus', 4, 4)),
          },
          {
            key: 6,
            label: intl.formatMessage(listItemMsg('extendedtaskstatus', 6, 6)),
          },
        ],
      });
    });
  }

  componentDidUpdate(prevProps) {
    const { show } = this.props;

    if (prevProps.show !== show) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(() => ({
        emails: [],
        emailAttachments: [],
        selectedSubtask: undefined,
        selectedTaskStatus: 0,
        loadingTemplate: false,
        showEmailEditor: false,
      }));
    }
  }

  static propTypes = {
    subtasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    onSave: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool,
  };

  static defaultProps = {
    show: false,
  };

  subtaskChanged = event => {
    const { subtasks } = this.props;
    const subtaskId = event.target.value;
    const subtask = subtasks.find(x => x.id === subtaskId);

    this.setState(state => ({
      ...state,
      selectedSubtask: subtask,
      showEmailEditor: false,
      sendEmail: false,
      emails: [],
    }));
  };

  taskStatusChanged = event => {
    const taskStatus = event.target.value;
    this.setState(state => ({
      ...state,
      selectedTaskStatus: taskStatus,
    }));
  };

  isFormInvalid = () => {
    const { selectedSubtask } = this.state;
    if (!selectedSubtask) return true;

    return false;
  };

  onSave = () => {
    const {
      selectedSubtask,
      selectedTaskStatus,
      showEmailEditor,
      emails,
      emailAttachments,
    } = this.state;
    const { onSave, intl } = this.props;
    this.setState({ touchedTask: true });

    if (!selectedSubtask || !selectedSubtask.id) return;

    const isFormValid = this.validate();

    let invalidEmailBody = false;

    if (showEmailEditor) {
      if (emails.length) {
        emails.map(x => {
          if (!x.content || x.content === '<p></p>') {
            invalidEmailBody = true;
          }
        });
      } else {
        invalidEmailBody = true;
      }
    }

    if (invalidEmailBody) {
      ToastrService.error(intl.formatMessage(messages.EmailContentRequired));
      return Promise.reject(new Error('invalid form'));
    }

    if (!isFormValid) return Promise.reject(new Error('invalid form'));

    const status = parseInt(selectedTaskStatus);

    const parameters = {
      mailTemplateId: selectedSubtask.mailTemplateId,
      subtaskTemplateId: selectedSubtask.id,
      desiredStatus: status === 4 || status === 6 ? 2 : status, // remap extended task status to normal status.
      markAsInapplicable: status === 4,
      markAsRejected: status === 6,
      sendEmail: showEmailEditor,
      emails,
      emailAttachments: emailAttachments.map(x => ({
        documentId: x.id,
        languageCode: x.languageCode,
      })),
    };

    return onSave(parameters);
  };

  closeModal = () => {
    const { onClose } = this.props;
    onClose();
  };

  loadEmailTemplates = (templateId, languageCodes) => {
    const { intl } = this.props;

    return Promise.all(
      languageCodes.map((languageCode, index) =>
        api.base
          .get(`${config.appUrls.api}/api/template/content/${templateId}/${languageCode}`)
          .then(({ data }) => ({
            ...data,
            initialValue: data.content,
            collapsed: index !== 0,
            documentDate: moment.utc(),
          }))
          // if no template was found in the language code, just return an empty object to
          // still be able to use the editor
          .catch(() => {
            const language = intl.formatMessage(
              listItemMsg('language', languageCode, languageCode),
            );
            const message = intl.formatMessage(messages.NoEmailTemplateFoundInLanguage, {
              language,
            });
            ToastrService.error(message);
            return {
              languageCode,
              content: '',
              title: '',
              collapsed: index !== 0,
              documentDate: moment.utc(),
            };
          }),
      ),
    );
  };
  getAttachments = (language, templateLanguageId) =>
    api.base
      .get(`${config.appUrls.documents}/api/Documents/Links/${templateLanguageId}/4`)
      .then(({ data }) => data.map(doc => this.mapAttachment(doc, language)));

  handleSendMailClick = () => {
    const { showEmailEditor, selectedSubtask } = this.state;
    const { relationLanguages } = this.props;
    const newShowMailEditor = !showEmailEditor;

    const loadEmailTemplate = newShowMailEditor && selectedSubtask.mailTemplateId;

    this.setState(state => ({
      ...state,
      showEmailEditor: newShowMailEditor,
      loadingTemplate: !!loadEmailTemplate,
      emails: [],
      emailAttachments: [],
    }));

    if (loadEmailTemplate) {
      this.loadEmailTemplates(selectedSubtask.mailTemplateId, relationLanguages).then(
        emailTemplates => {
          this.setState(state => ({
            ...state,
            emails: emailTemplates,
            loadingTemplate: false,
            autoLinkDocumentTypes: emailTemplates[0].autoLinkDocumentTypes,
          }));
          Promise.all(
            emailTemplates.filter(x => !!x.id).map(x => this.getAttachments(x.languageCode, x.id)),
          ).then(([...results]) => {
            this.setState(state => ({
              emailAttachments: [...state.emailAttachments, ...results.flat()],
            }));
          });
        },
        error => {
          console.error(error);
          this.setState(state => ({
            ...state,
            loadingTemplate: false,
          }));
        },
      );
    }
  };

  emailTitleChanged = (email, title) => {
    const { emails } = this.state;

    this.setState({
      emails: emails.map(e => {
        if (e.languageCode === email.languageCode)
          return {
            ...e,
            title,
          };
        return e;
      }),
    });
  };

  emailContentChanged = (email, content) => {
    const { emails } = this.state;

    this.setState({
      emails: emails.map(e => {
        if (e.languageCode === email.languageCode)
          return {
            ...e,
            content,
          };
        return e;
      }),
    });
  };

  onEditorStateChange = (email, content) => {
    const { emails } = this.state;

    this.setState({
      emails: emails.map(e => {
        if (e.languageCode === email.languageCode)
          return {
            ...e,
            content,
          };
        return e;
      }),
    });
  };

  renderSingleEmail = (email, languageCode) => {
    const documentUploadEndpoint = `${config.appUrls.documents}/api/documents?documentType=15`;
    const { emailAttachments } = this.state;
    return (
      <EmailEditor
        id={languageCode}
        title={email.title}
        onTitleChanged={title => this.emailTitleChanged(email, title)}
        initialValue={email.initialValue}
        onContentChanged={content => this.onEditorStateChange(email, content)}
        onAttachmentUploaded={(error, file) =>
          this.onDocumentUploaded(error, file, email.languageCode)
        }
        attachmentUploadEndpoint={documentUploadEndpoint}
        attachments={emailAttachments.filter(x => x.languageCode === email.languageCode)}
        onDeleteAttachment={this.deleteAttachment}
        documentDate={email.documentDate}
        onDocumentDateChanged={documentDate =>
          this.setState(state => ({
            ...state,
            emails: state.emails.map(x => {
              if (x.languageCode === email.languageCode) {
                return {
                  ...x,
                  documentDate,
                };
              }
              return x;
            }),
          }))
        }
      />
    );
  };

  emailCollapseClicked = email => {
    const { emails } = this.state;

    const emailsCopy = [...emails];

    const item = emailsCopy.find(x => x.languageCode === email.languageCode);

    item.collapsed = !item.collapsed;

    this.setState(state => ({
      ...state,
      emails: emailsCopy,
    }));
  };

  mapAttachment = (document, languageCode) => ({
    ...document,
    languageCode,
  });

  onDocumentUploaded = (error, document, languageCode) => {
    const { intl } = this.props;

    if (error) {
      log.error(error);
      ToastrService.error(intl.formatMessage(messages.MsgEmailAttachmentUploadFailed));
      return;
    }

    const emailAttachment = this.mapAttachment(document, languageCode);

    this.setState(state => ({
      ...state,
      emailAttachments: [emailAttachment, ...state.emailAttachments],
    }));
  };
  deleteAttachment = attachment => {
    if (
      AuthService.hasPermission({
        functionality: functionalities.Documents,
        privilege: privileges.Delete,
      }) &&
      attachment.documentType === 15
    ) {
      api.base.delete(`${config.appUrls.documents}/api/documents/${attachment.id}`).then(() => {
        this.setState(state => ({
          emailAttachments: state.emailAttachments.filter(x => x.id !== attachment.id),
        }));
      });
    } else {
      this.setState(state => ({
        emailAttachments: state.emailAttachments.filter(x => x.id !== attachment.id),
      }));
    }
  };

  renderMultipleEmails = emails => {
    const { intl } = this.props;
    return emails.map(email => (
      <CollapsablePanel
        key={email.languageCode}
        collapsed={email.collapsed}
        collapseChanged={() => this.emailCollapseClicked(email)}
        title={
          <span className="margin10L">
            {intl.formatMessage(listItemMsg('language', email.languageCode, email.languageCode))}
          </span>
        }
      >
        <div>{this.renderSingleEmail(email, email.languageCode)}</div>
      </CollapsablePanel>
    ));
  };

  validate() {
    const { selectedSubtask } = this.state;

    if (!selectedSubtask || !selectedSubtask.id) {
      return false;
    }
    return true;
  }

  render() {
    const { show, intl, subtasks } = this.props;
    const {
      selectedSubtask,
      selectedTaskStatus,
      showEmailEditor,
      loadingTemplate,
      emails,
      touchedTask,
      taskstatuses,
      autoLinkDocumentTypes,
    } = this.state;

    const showMailCheckbox =
      selectedSubtask && (selectedSubtask.type === 3 || selectedSubtask.type === 4);
    const taskClassName = classNames('form-group', {
      'has-error': touchedTask && (!selectedSubtask || !selectedSubtask.id),
    });

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.ChangeTaskStatus)}
        show={show}
        size={showEmailEditor ? 'lg' : ''}
        onClose={this.closeModal}
        disabled={!this.validate()}
        onSave={this.onSave}
      >
        {show && (
          <form ref={this.form}>
            <div className="form-horizontal">
              <div className={taskClassName}>
                <label className="control-label col-sm-3">
                  {intl.formatMessage(messages.Subtask)}
                </label>
                <div className="col-md-6">
                  {subtasks && (
                    <select
                      onChange={this.subtaskChanged}
                      className="form-control form-field"
                      value={(selectedSubtask && selectedSubtask.id) || ''}
                    >
                      {!selectedSubtask && (
                        <option value="">{intl.formatMessage(messages.SelectASubtask)}</option>
                      )}
                      {subtasks.map(subtask => (
                        <option key={subtask.id} value={subtask.id}>
                          {intl.formatMessage(
                            listItemMsg('taskTemplate', subtask.id, subtask.name),
                          )}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="control-label col-sm-3">
                  {intl.formatMessage(messages.TaskStatus)}
                </label>
                <div className="col-md-6">
                  <select
                    onChange={this.taskStatusChanged}
                    className="form-control"
                    value={selectedTaskStatus || ''}
                  >
                    {taskstatuses &&
                      taskstatuses.map(taskstatus => (
                        <option value={taskstatus.key}>{taskstatus.label}</option>
                      ))}
                  </select>
                </div>
              </div>
              {showMailCheckbox && (
                <div className="form-group">
                  <Checkbox
                    extraClass="col-sm-offset-3 col-md-8"
                    id="sendMailCheckbox"
                    value={showEmailEditor}
                    onClick={() => this.handleSendMailClick()}
                    text={intl.formatMessage(messages.SendEmail)}
                  />
                </div>
              )}
              {loadingTemplate && <LoadingAnimation />}

              {showEmailEditor && !loadingTemplate && (
                <div className="form-group padding10T">
                  <span className="bold col-sm-offset-3 col-md-8">
                    {intl.formatMessage(messages.PlaceholdersWillBeResolvedByAdminPulse)}
                  </span>
                </div>
              )}
              {showEmailEditor &&
                !loadingTemplate &&
                autoLinkDocumentTypes &&
                autoLinkDocumentTypes.length > 0 && (
                  <div className="form-group">
                    <span className=" col-sm-offset-3 col-md-8">
                      <span className="bold">
                        {intl.formatMessage(
                          messages.DocumentsOfTheFollowingTypeWillBeLinkedIfPresent,
                        )}
                      </span>

                      <ul>
                        {autoLinkDocumentTypes.map(x => (
                          <li>{intl.formatMessage(listItemMsg('documentType', x, x))}</li>
                        ))}
                      </ul>
                    </span>
                  </div>
                )}

              {showEmailEditor &&
                !loadingTemplate &&
                emails &&
                (emails.length === 1
                  ? this.renderSingleEmail(emails[0])
                  : this.renderMultipleEmails(emails))}
            </div>
          </form>
        )}
      </OkCancelModal>
    );
  }
}
